import { createContext, useContext, useEffect, useId, useRef } from 'react';
import {
    ActionDependant,
    evaluateTemplateValue,
    GlobalDataContext,
    TemplateContext,
    useEvaluatedAttributes,
    View,
} from '@ea-lab/reactive-json';

const FieldStatusContext = createContext(null);

/**
 * Wrapper component that reads per-field validation errors from a data path
 * and provides the error for a specific field via React context.
 *
 * Expects the errors data to be an object keyed by field name, e.g.:
 *   { "name": "This value is required.", "account": "Invalid type." }
 *
 * YAML usage:
 *   - type: FieldStatus
 *     field: "name"
 *     dataLocation: ~~.newProject.errors
 *     content:
 *       - type: TextField
 *         dataLocation: ~~.newProject.data.name
 *       - type: FieldStatusMessage
 *         attributes:
 *           class: "text-red-600 text-xs mt-1"
 */
export const FieldStatus = ({ props, currentData, path }) => {
    const globalDataContext = useContext(GlobalDataContext);
    const templateContext = useContext(TemplateContext);
    const wrapperRef = useRef(null);
    const messageId = useId();

    const errors = evaluateTemplateValue({
        valueToEvaluate: props.dataLocation,
        globalDataContext,
        templateContext,
    });

    const fieldError = errors && typeof errors === 'object'
        ? errors[props.field] || null
        : null;

    // A11y (WCAG 1.3.1 / 3.3.1): a generic wrapper cannot know which nested
    // child is the actual control, so we resolve it from the rendered DOM and
    // toggle aria-invalid + aria-describedby (pointing at the FieldStatusMessage).
    // The field components never set aria-invalid, so React does not clobber it.
    // aria-describedby, on the other hand, may well be declared in the YAML to
    // point at a hint (accepted formats…): the message id is APPENDED to it and
    // removed again on its own, so the author's hint survives the error cycle.
    useEffect(() => {
        const root = wrapperRef.current;
        if (!root) return;
        const control = root.querySelector('input, select, textarea, [contenteditable="true"]');
        if (!control) return;
        const described = (control.getAttribute('aria-describedby') || '')
            .split(/\s+/)
            .filter((id) => id && id !== messageId);
        if (fieldError) {
            control.setAttribute('aria-invalid', 'true');
            if (document.getElementById(messageId)) {
                control.setAttribute('aria-describedby', [...described, messageId].join(' '));
            }
        } else {
            control.removeAttribute('aria-invalid');
            if (described.length) {
                control.setAttribute('aria-describedby', described.join(' '));
            } else {
                control.removeAttribute('aria-describedby');
            }
        }
    }, [fieldError, messageId]);

    return (
        <ActionDependant {...props}>
            <FieldStatusContext.Provider value={{ error: fieldError, field: props.field, messageId }}>
                <div ref={wrapperRef} className={fieldError ? 'field-status-error' : undefined}>
                    {Array.isArray(props.content) && props.content.map((child, index) => (
                        <View
                            key={index}
                            currentData={currentData?.content?.[index]}
                            datafield={`content.${index}`}
                            path={`${path}.content.${index}`}
                            props={child}
                        />
                    ))}
                </div>
            </FieldStatusContext.Provider>
        </ActionDependant>
    );
};

/**
 * Displays the error message for the enclosing FieldStatus.
 * Renders nothing when no error is present.
 *
 * Must be used inside a FieldStatus wrapper.
 */
export const FieldStatusMessage = ({ props }) => {
    const ctx = useContext(FieldStatusContext);
    const attributes = useEvaluatedAttributes(props.attributes);

    if (!ctx?.error) return null;

    const { class: className, ...restAttrs } = attributes || {};

    return (
        <ActionDependant {...props}>
            <div id={ctx.messageId} className={className} {...restAttrs}>{ctx.error}</div>
        </ActionDependant>
    );
};
